/**
 * CurrencyExchangeField — Shared currency selector + conditional exchange rate input
 *
 * Rules:
 * - Label: "Ngoại tệ" (never "Tiền tệ" or "Currency")
 * - VND: exchangeRate = 1, hide rate input
 * - USD: default 25400, show editable rate input
 * - JPY: default 165, show editable rate input
 *
 * Usage:
 *   <CurrencyExchangeField
 *     currency={form.getFieldValue('currency')}
 *     exchangeRate={form.getFieldValue('exchangeRate')}
 *     onCurrencyChange={(v) => { form.setFieldValue('currency', v); }}
 *     onExchangeRateChange={(v) => { form.setFieldValue('exchangeRate', v); }}
 *   />
 */

import { Label } from '@/components/ui/label';
import { NumericInput } from '@/components/shared/NumericInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useConfigSuggestions } from '@/hooks/useConfigSuggestions';

export const DEFAULT_RATES: Record<string, number> = { VND: 1, USD: 25400, JPY: 165 };

interface CurrencyExchangeFieldProps {
    currency: string;
    exchangeRate: number;
    onCurrencyChange: (currency: string, defaultRate: number) => void;
    onExchangeRateChange: (rate: number) => void;
    /** Show currency and rate on the same row (default: true) */
    inline?: boolean;
    /** Hide the rate input field entirely */
    hideRateInput?: boolean;
    /** Hide the label */
    hideLabel?: boolean;
}

export default function CurrencyExchangeField({
    currency,
    exchangeRate,
    onCurrencyChange,
    onExchangeRateChange,
    inline = true,
    hideRateInput = false,
    hideLabel = false,
}: CurrencyExchangeFieldProps) {
    const currencyOptions = useConfigSuggestions('CURRENCIES', ['VND', 'USD', 'JPY']);

    const handleCurrencyChange = (v: string) => {
        const rate = DEFAULT_RATES[v] ?? 1;
        onCurrencyChange(v, rate);
        onExchangeRateChange(rate);
    };

    const currencySelect = (
        <div className="space-y-1">
            {!hideLabel && <Label>Ngoại tệ</Label>}
            <Select value={currency || 'VND'} onValueChange={handleCurrencyChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                    {currencyOptions.map((c) => (
                        <SelectItem key={c} value={c}>{c === 'VND' ? 'VNĐ' : c}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );

    const rateInput = (!hideRateInput && currency !== 'VND') ? (
        <div className="space-y-1">
            {!hideLabel && <Label>Tỷ giá</Label>}
            <NumericInput
                value={exchangeRate}
                onChange={onExchangeRateChange}
                decimals={0}
                placeholder="25.400"
            />
        </div>
    ) : null;

    if (inline) {
        return (
            <div className="flex gap-2">
                <div className={hideRateInput ? "flex-1" : "flex-1"}>{currencySelect}</div>
                {rateInput && <div className="flex-1">{rateInput}</div>}
            </div>
        );
    }

    return (
        <>
            {currencySelect}
            {rateInput}
        </>
    );
}
